import classnames from "classnames";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";

import { LabOrder, Stain } from "pwapi";

import { partialUpdateLabOrder } from "shared/actions/labOrdersActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import useLabOrderExternalLabNames from "shared/hooks/useLabOrderExternalLabNames";
import { useCanAccessStainOrdering } from "shared/hooks/usePermissions";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import selectStainByLabStainId from "shared/selectors/selectStainByLabStainId";
import {
  noChargeReasonEnumToLabel,
  splitFlagReason,
} from "shared/utils/labOrderUtils";

import LinkButton from "components/LinkButton";
import { selectAutoCptCodesEnabled } from "selectors/autoCptCodesEnabled";
import { useAppDispatch, useAppSelector } from "store/hooks";

import { getOrderStatus } from "../../../admin/editCasePanel/Orders.helpers";
import OrderIcon from "../../../OrderIcon";
import { SlideFlagIcon } from "../../SlideFlagIcon";

type ActiveStainOrderProps = {
  order: LabOrder;
  cancelOrder: Function;
  hideStatusIcons?: boolean;
};

const ActiveStainOrder = ({
  order,
  cancelOrder,
  hideStatusIcons,
}: ActiveStainOrderProps) => {
  const dispatch = useAppDispatch();
  const [isCancelable, setIsCancelable] = useState<boolean>(false);
  const inadequateOrdersFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "inadequateOrders")
  );
  const noChargeReasonFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "noChargeReason")
  );
  const canAccessStainOrdering = useCanAccessStainOrdering();
  const autoCptCodesEnabled = useAppSelector(selectAutoCptCodesEnabled);
  const slides = useAppSelector(({ caseDetail }) => caseDetail.slides);
  const slide = slides.find((s) => s.id === order.slide_id);
  const block = useAppSelector(({ caseDetail }) =>
    caseDetail.blocks?.find((b) => b.id === slide?.block_obj_id)
  );
  const specimen = useAppSelector(({ caseDetail }) =>
    caseDetail.specimens?.find((s) => s.id === block?.specimen_id)
  );

  const flags = slides.map((s) => s.flags).flat();

  const flag = flags?.find((f) => f.order_id === order.id);

  const slideStain = useAppSelector(
    ({ stains }) => stains[slide?.stain_id || 0]
  );

  const externalLabNames = useLabOrderExternalLabNames(order);
  const stain = useAppSelector(selectStainByLabStainId(order.lab_stain_id));
  const labStain = useAppSelector(({ labStains }) =>
    order.lab_stain_id ? labStains[order.lab_stain_id] : undefined
  );

  const updatingOrder = useIsLoading(partialUpdateLabOrder, { id: order.id });

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined = undefined;
    // TODO: Skip when order.sent_at is defined. Need to expose this in the API first.
    // This is low priority because it only happens with seeded data.
    if (
      order.created_at &&
      !order.completed_at &&
      !order.cancelled_at &&
      order.transmitting_at
    ) {
      const transmission_time = DateTime.fromISO(order.transmitting_at)
        .diffNow()
        .toObject().milliseconds;
      if (transmission_time && transmission_time > 0) {
        setIsCancelable(true);
        timer = setTimeout(() => {
          setIsCancelable(false);
        }, transmission_time);
      }
    }

    return () => {
      timer && clearTimeout(timer);
    };
  }, [
    order.created_at,
    order.completed_at,
    order.cancelled_at,
    order.transmitting_at,
  ]);

  const handleInadequateOrder = (id: number, inadequate: boolean) => {
    dispatch(
      partialUpdateLabOrder({
        id,
        requestBody: {
          inadequate,
        },
      })
    );
  };

  const handleNoChargeReasonOrder = (
    id: number,
    no_charge_reason: LabOrder.no_charge_reason
  ) => {
    dispatch(
      partialUpdateLabOrder({
        id,
        requestBody: {
          no_charge_reason,
        },
      })
    );
  };

  const showNoChargeReason = noChargeReasonFlag || autoCptCodesEnabled;

  return (
    <div
      className={classnames("stainDetails__container", {
        "stainDetails__container--completed":
          order.completed_at && !order.inadequate && !order.no_charge_reason,
        "stainDetails__container--inadequate":
          order.completed_at && (order.inadequate || order.no_charge_reason),
        "stainDetails__container--cancelled":
          !order.completed_at && order.cancelled_at,
        "stainDetails__container--started":
          !order.completed_at && !order.cancelled_at,
      })}
    >
      <div className="stainDetails__container__order">
        {order.type === "rescan" ? (
          <div>
            Rescan{" "}
            <strong>
              {specimen?.name || ""}
              {block?.name || ""}.S{slide?.slide_number || 1}{" "}
              {slideStain?.abbreviation}
            </strong>
          </div>
        ) : (
          <div>
            <strong>{stain?.name}</strong>{" "}
            {stain?.category !== Stain.category.ROUTINE && (
              <>
                <abbr>
                  (
                  <code>{labStain?.label_override || stain?.abbreviation}</code>
                  )
                </abbr>
                <br />

                <span className="stainDetails__container__category">
                  {stain?.category}
                </span>
              </>
            )}
          </div>
        )}

        <div>
          {externalLabNames && (
            <div className="stainDetails__external_lab">{externalLabNames}</div>
          )}
        </div>

        <div>
          {isCancelable && canAccessStainOrdering && (
            <LinkButton onClick={() => cancelOrder(order)}>Cancel</LinkButton>
          )}
        </div>

        {!hideStatusIcons && !isCancelable && (
          <div className="stainDetails__container__status">
            <OrderIcon
              data-tip=""
              data-for={`order-${order.id}-status`}
              status={getOrderStatus(order)}
            />
            <ReactTooltip id={`order-${order.id}-status`}>
              {order.completed_at
                ? order.inadequate || order.no_charge_reason
                  ? `${
                      order.no_charge_reason
                        ? noChargeReasonEnumToLabel(order.no_charge_reason)
                        : "Inadequately"
                    } Completed ${new Date(
                      order.completed_at
                    ).toLocaleString()}`
                  : `Order Completed ${new Date(
                      order.completed_at
                    ).toLocaleString()}`
                : order.cancelled_at
                ? `Order Cancelled ${new Date(
                    order.cancelled_at
                  ).toLocaleString()}`
                : "Order Placed"}
            </ReactTooltip>
            {order.completed_at &&
              inadequateOrdersFlag &&
              !showNoChargeReason && (
                <label>
                  <input
                    type="checkbox"
                    checked={order.inadequate}
                    disabled={updatingOrder}
                    onChange={(e) =>
                      handleInadequateOrder(order.id!, e.target.checked)
                    }
                  />{" "}
                  Inadequate
                </label>
              )}
            {order.completed_at && showNoChargeReason && (
              <select
                disabled={updatingOrder}
                value={order.no_charge_reason || ""}
                onChange={(e) =>
                  handleNoChargeReasonOrder(
                    order.id!,
                    (e.target.value as LabOrder.no_charge_reason) || null
                  )
                }
                className="stainDetails__no_charge_reason"
              >
                <option value=""></option>
                {Object.values(LabOrder.no_charge_reason).map(
                  (no_charge_reason) => (
                    <option key={no_charge_reason} value={no_charge_reason}>
                      {noChargeReasonEnumToLabel(no_charge_reason)}
                    </option>
                  )
                )}
              </select>
            )}
          </div>
        )}
      </div>

      {flag && (
        <div>
          <SlideFlagIcon />
          &nbsp;
          <span className="stainDetails__container__note">
            {splitFlagReason(flag.reason)}
          </span>
        </div>
      )}
      {order.note && (
        <span className="stainDetails__container__note">
          &rdquo;{order.note}&ldquo;
        </span>
      )}

      <div className="stainDetails__container__info">
        <span className="stainDetails__container__user">
          {order.user?.first_name} {order.user?.last_name}
        </span>
        {order.created_at && (
          <span className="stainDetails__container__datetime">
            {DateTime.fromISO(order.created_at).toLocaleString(
              DateTime.DATETIME_SHORT
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default ActiveStainOrder;
